import { Link } from 'react-router-dom';
import { Callout, DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'tray', label: 'Running in the tray' },
  { id: 'export', label: 'Export from results' },
  { id: 'included', label: 'Included with every license' },
];

export default function Body() {
  return (
    <>
      <DocSection id="overview" title="Overview">
        <p>
          The desktop app <strong>is</strong> ClipRadar — there's no separate engine running underneath it.
          When you open the <Link to="/docs/platforms/premiere-panel">Premiere panel</Link>, you're not
          launching a second, lighter tool: you're looking at the same app, mounted inside Premiere Pro. One
          library, one set of settings, one license — whichever window you search from.
        </p>
        <p>
          Most editors run ClipRadar as a normal Windows app: launch it from the Start menu, build your
          library in the <strong>Media</strong> tab, and search. The panel is there for when you want results
          placed straight onto a live timeline without leaving Premiere — but the standalone app works fully
          on its own, with nothing extra to install.
        </p>
        <DocShot
          slug="standalone-app"
          shot="tray-menu"
          alt="The ClipRadar icon in the Windows system tray with its right-click menu open, showing Open ClipRadar and Quit ClipRadar"
          caption="The ClipRadar icon sits in the system tray any time the app is running — even with its window closed."
          callouts={[
            { n: 1, label: 'Open ClipRadar — brings the window back' },
            { n: 2, label: 'Quit ClipRadar — actually closes the app' },
          ]}
        />
      </DocSection>

      <DocSection id="tray" title="Running in the tray">
        <p>
          Closing the ClipRadar window doesn't quit the app — it hides to the Windows system tray, and
          ClipRadar keeps running in the background. That's deliberate: the Premiere panel talks to the
          desktop app's local server, and an Analyze pass in progress keeps working, so both need ClipRadar
          alive even when its window is out of the way.
        </p>
        <Callout>
          The first time you close the window in a session, a tray balloon confirms what just happened:
          "ClipRadar keeps running in the tray so search stays available. Right-click the tray icon to quit."
        </Callout>
        <p>
          Right-click the tray icon for two options: <span style={kbd}>Open ClipRadar</span> brings the window
          back, and <span style={kbd}>Quit ClipRadar</span> actually shuts the app down. If an Analyze pass is
          running when you choose <span style={kbd}>Quit ClipRadar</span>, ClipRadar checks first — "Quitting
          now stops the current job and loses its progress. Quit anyway?" — with <span style={kbd}>Quit</span>{' '}
          and <span style={kbd}>Keep running</span> buttons, so a background job never gets killed by accident.
        </p>
        <DocShot
          slug="standalone-app"
          shot="tray-balloon"
          alt='The Windows tray balloon reading "ClipRadar keeps running in the tray so search stays available. Right-click the tray icon to quit."'
          caption="Shown once per session, the first time you close the ClipRadar window."
        />
      </DocSection>

      <DocSection id="export" title="Export from results">
        <p>
          Search results don't have to stay inside ClipRadar. Select one or more result cards and click{' '}
          <span style={kbd}>Export N to timeline</span> in the results toolbar to build a timeline XML file —
          trimmed to each matched moment (or to whatever range you set with <span style={kbd}>Adjust</span> on
          a card first) — that <strong>imports</strong> into Premiere Pro, DaVinci Resolve, or Final Cut. It's
          an import, not a live connection: for clips to land on an already-open Premiere timeline as you
          search, use the{' '}
          <Link to="/docs/platforms/premiere-panel">Premiere panel</Link> instead.
        </p>
        <p>
          A single card can also be exported on its own from its hover actions. After exporting, the same
          spot on the card offers <span style={kbd}>Show in folder</span> and <span style={kbd}>Re-export</span>{' '}
          — useful if you adjust the trim and want a fresh file. For the full trim-then-export workflow, see{' '}
          <Link to="/docs/guides/build-a-rough-cut">From search results to a rough cut</Link>.
        </p>
        <DocShot
          slug="standalone-app"
          shot="export"
          alt="The search results toolbar with cards selected and the Export N to timeline button, followed by the exported card's Show in folder and Re-export actions"
          caption="Select results, export a timeline, then jump straight to the file or re-export after a trim."
          callouts={[
            { n: 1, label: 'Select the result cards you want' },
            { n: 2, label: 'Export N to timeline — builds the XML file' },
            { n: 3, label: 'Show in folder / Re-export — once it’s done' },
          ]}
        />
      </DocSection>

      <DocSection id="included" title="Included with every license">
        <p>
          The desktop app isn't a trimmed-down companion to some other product — it's what your license
          activates, full stop. The <Link to="/docs/platforms/premiere-panel">Premiere panel</Link> installs
          on top of it as a free add-on: there's nothing extra to buy to search from inside Premiere Pro, and
          no separate account or key for the panel. See{' '}
          <Link to="/docs/concepts/licensing">Licensing &amp; activation</Link> for how a license key activates
          a machine.
        </p>
      </DocSection>
    </>
  );
}
